/**
 * EditServiceModal
 *
 * Full editor for an existing CustomerService — covers everything that's not
 * the monthly price (price has its own audit-logged modal in CustomerServicesPage).
 *
 * Use case: tech installed a Starlink kit but didn't capture the kit number on
 * the day, or a router gets swapped under warranty. Office needs to update
 * serial numbers, account email, plan name, contract end date, etc. without
 * deleting and re-creating the service.
 *
 * Pre-populates from the service's existing metadata + top-level fields.
 * Writes back via updateServiceMetadata + a top-level updateDoc for the few
 * fields that live outside metadata (notes, accountNumber, planName).
 */

import { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import { X, Wifi, Save, Pencil, MapPin } from 'lucide-react';
import { CustomerService, ServicePlan } from '../types/schema';
import { updateServiceMetadata, updateServicePrice } from '../services/customer-service.service';
import { useServicePlans } from '../hooks/useServicePlans';
import { formatCurrency, addGST } from '@/utils/pricing';
import { planLabelWithSpeed } from '@/utils/planSpeed';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import {
  ServiceAddressPicker,
  ServiceAddressSelection,
  serviceAddressFromFields,
  commitServiceAddressToBook,
} from './ServiceAddressPicker';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  service: CustomerService | null;
  onSaved?: () => void;
}

export function EditServiceModal({ isOpen, onClose, service, onSaved }: Props) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Starlink
  const [kitNumber, setKitNumber] = useState('');
  const [starlinkAccountEmail, setStarlinkAccountEmail] = useState('');
  const [starlinkAccountNumber, setStarlinkAccountNumber] = useState('');
  const [starlinkModel, setStarlinkModel] = useState<'Standard' | 'Mini' | 'Gen 1' | 'Gen 2' | 'Gen 3' | ''>('');

  // 4G/5G
  const [carrier, setCarrier] = useState<'Telstra' | 'Optus' | ''>('');
  const [simNumber, setSimNumber] = useState('');
  const [serviceNumber, setServiceNumber] = useState('');

  // NBN
  const [nbnServiceId, setNbnServiceId] = useState('');
  const [nbnLocationId, setNbnLocationId] = useState('');
  const [technology, setTechnology] = useState('');

  // OptiComm (private fibre wholesaler — FTTP only, no NBN-style tech tiers)
  const [opticommServiceId, setOpticommServiceId] = useState('');
  const [opticommOrderNumber, setOpticommOrderNumber] = useState('');

  // WiFi Extension
  const [apCount, setApCount] = useState('');
  const [apSerials, setApSerials] = useState('');
  const [switchModel, setSwitchModel] = useState('');
  const [switchSerial, setSwitchSerial] = useState('');

  // Smart Home
  const [hubBrand, setHubBrand] = useState('');
  const [hubModel, setHubModel] = useState('');
  const [hubSerial, setHubSerial] = useState('');
  const [deviceCount, setDeviceCount] = useState('');

  // Security
  const [panelSerial, setPanelSerial] = useState('');
  const [cameraCount, setCameraCount] = useState('');
  const [sensorCount, setSensorCount] = useState('');
  const [nvrModel, setNvrModel] = useState('');
  const [nvrSerial, setNvrSerial] = useState('');

  // VoIP
  const [sasbossCustomerName, setSasbossCustomerName] = useState('');
  const [mainNumber, setMainNumber] = useState('');
  const [handsetCount, setHandsetCount] = useState('');
  const [handsetLocations, setHandsetLocations] = useState('');
  const [portingRequired, setPortingRequired] = useState(false);
  const [numbersToPort, setNumbersToPort] = useState('');
  const [portingStatus, setPortingStatus] = useState<'' | 'pending' | 'submitted' | 'approved' | 'completed'>('');
  const [callFlowNotes, setCallFlowNotes] = useState('');

  // Common
  const [accountNumber, setAccountNumber] = useState('');
  const [planName, setPlanName] = useState('');
  const [contractEndDate, setContractEndDate] = useState('');
  const [notes, setNotes] = useState('');

  // Plan reassignment — pick from the CURRENT catalogue so plan name + price
  // move together (price change routes through the audited updateServicePrice).
  // '' = custom / keep the free-text name below.
  const [selectedPlanId, setSelectedPlanId] = useState('');

  // Service address — pick from the customer's saved addresses or enter a new one
  const [addressSel, setAddressSel] = useState<ServiceAddressSelection>(serviceAddressFromFields({}));

  // Active plans for this service type. The "4G/5G" service type spans two
  // plan serviceTypes, mirroring AddCustomerServiceModal.
  const planServiceType = useMemo(() => {
    const t = service?.serviceType;
    if (!t) return undefined;
    if (t === '4G/5G') return ['4G Mobile', '4G Data'] as ServicePlan['serviceType'][];
    if (['Starlink', 'NBN', 'OptiComm', 'VoIP'].includes(t)) return t as ServicePlan['serviceType'];
    return undefined;
  }, [service?.serviceType]);

  const { plans: catalogPlans } = useServicePlans({
    serviceType: planServiceType,
    activeOnly: true,
    sortByPromoted: true,
  });

  const selectedCatalogPlan = useMemo(
    () => (selectedPlanId ? catalogPlans.find((p) => p.id === selectedPlanId) || null : null),
    [catalogPlans, selectedPlanId],
  );

  // Price preview for the picked plan (recurring services only).
  const planPriceChange = useMemo(() => {
    if (!service || !selectedCatalogPlan || service.billingType === 'one-time') return null;
    if (selectedCatalogPlan.id === service.planId) return null;
    const current = typeof service.monthlyPrice === 'number' ? service.monthlyPrice : null;
    if (current !== null && current === selectedCatalogPlan.sellPrice) return null;
    return { from: current, to: selectedCatalogPlan.sellPrice };
  }, [service, selectedCatalogPlan]);

  // Hydrate from service whenever the modal opens with a new service
  useEffect(() => {
    if (!isOpen || !service) return;
    const m = service.metadata || {};

    setKitNumber(m.kitNumber || '');
    setStarlinkAccountEmail(m.starlinkAccountEmail || '');
    setStarlinkAccountNumber(m.starlinkAccountNumber || '');
    setStarlinkModel(m.starlinkModel || '');

    setCarrier(m.carrier || '');
    setSimNumber(m.simNumber || '');
    setServiceNumber((m as { serviceNumber?: string }).serviceNumber || '');

    setNbnServiceId(m.nbnServiceId || '');
    setNbnLocationId(m.nbnLocationId || '');
    setTechnology(m.technology || '');

    setOpticommServiceId(m.opticommServiceId || '');
    setOpticommOrderNumber(m.opticommOrderNumber || '');

    setApCount(m.accessPointCount != null ? String(m.accessPointCount) : '');
    setApSerials((m.accessPointSerials || []).join(', '));
    setSwitchModel(m.switchModel || '');
    setSwitchSerial(m.switchSerial || '');

    setHubBrand(m.hubBrand || '');
    setHubModel(m.hubModel || '');
    setHubSerial(m.hubSerialNumber || '');
    setDeviceCount(m.deviceCount != null ? String(m.deviceCount) : '');

    setPanelSerial(m.panelSerialNumber || '');
    setCameraCount(m.cameraCount != null ? String(m.cameraCount) : '');
    setSensorCount(m.sensorCount != null ? String(m.sensorCount) : '');
    setNvrModel(m.nvrModel || '');
    setNvrSerial(m.nvrSerial || '');

    setSasbossCustomerName(m.sasbossCustomerName || '');
    setMainNumber(m.mainNumber || '');
    setHandsetCount(m.handsetCount != null ? String(m.handsetCount) : '');
    setHandsetLocations(m.handsetLocations || '');
    setPortingRequired(!!m.portingRequired);
    setNumbersToPort((m.numbersToPort || []).join(', '));
    setPortingStatus(m.portingStatus || '');
    setCallFlowNotes(m.callFlowNotes || '');

    setAccountNumber(service.accountNumber || m.accountNumber || '');
    setPlanName(service.planName || m.planName || '');
    setSelectedPlanId(service.planId || '');
    setContractEndDate(m.contractEndDate || '');
    setNotes(service.notes || '');

    setAddressSel(serviceAddressFromFields({
      address: service.address,
      city: service.city,
      state: service.state,
      zipCode: service.zipCode,
    }));

    setError(null);
  }, [isOpen, service?.id]);

  if (!isOpen || !service) return null;

  const serviceType = service.serviceType;

  const handleSave = async () => {
    if (!service.id) {
      setError('Service has no ID — cannot save');
      return;
    }
    setError(null);
    setIsSubmitting(true);

    try {
      // Preserve any metadata fields we don't expose in the form (e.g. NBN
      // technical fields like ntdId, downloadSpeedKbps, Starlink billing
      // fields that get auto-populated from email parsing). Spread first,
      // then override with form values.
      const metadata: CustomerService['metadata'] = {
        ...(service.metadata || {}),
      };

      // --- Service-type-specific fields ---
      if (serviceType === 'Starlink') {
        metadata.kitNumber = kitNumber.trim() || undefined;
        metadata.starlinkAccountEmail = starlinkAccountEmail.trim() || undefined;
        metadata.starlinkAccountNumber = starlinkAccountNumber.trim() || undefined;
        metadata.starlinkModel = starlinkModel || undefined;
      } else if (serviceType === '4G/5G') {
        metadata.carrier = carrier || undefined;
        metadata.simNumber = simNumber.trim() || undefined;
        (metadata as { serviceNumber?: string }).serviceNumber = serviceNumber.trim() || undefined;
      } else if (serviceType === 'NBN') {
        metadata.nbnServiceId = nbnServiceId.trim() || undefined;
        metadata.nbnLocationId = nbnLocationId.trim() || undefined;
        metadata.technology = technology.trim() || undefined;
      } else if (serviceType === 'OptiComm') {
        metadata.opticommServiceId = opticommServiceId.trim() || undefined;
        metadata.opticommOrderNumber = opticommOrderNumber.trim() || undefined;
      } else if (serviceType === 'WiFi Extension') {
        metadata.accessPointCount = apCount ? parseInt(apCount, 10) : undefined;
        metadata.accessPointSerials = apSerials
          ? apSerials.split(',').map((s: string) => s.trim()).filter(Boolean)
          : undefined;
        metadata.switchModel = switchModel.trim() || undefined;
        metadata.switchSerial = switchSerial.trim() || undefined;
      } else if (serviceType === 'Smart Home') {
        metadata.hubBrand = hubBrand.trim() || undefined;
        metadata.hubModel = hubModel.trim() || undefined;
        metadata.hubSerialNumber = hubSerial.trim() || undefined;
        metadata.deviceCount = deviceCount ? parseInt(deviceCount, 10) : undefined;
      } else if (serviceType === 'Security') {
        metadata.panelSerialNumber = panelSerial.trim() || undefined;
        metadata.cameraCount = cameraCount ? parseInt(cameraCount, 10) : undefined;
        metadata.sensorCount = sensorCount ? parseInt(sensorCount, 10) : undefined;
        metadata.nvrModel = nvrModel.trim() || undefined;
        metadata.nvrSerial = nvrSerial.trim() || undefined;
      } else if (serviceType === 'VoIP') {
        metadata.sasbossCustomerName = sasbossCustomerName.trim() || undefined;
        metadata.mainNumber = mainNumber.trim() || undefined;
        metadata.handsetCount = handsetCount ? parseInt(handsetCount, 10) : undefined;
        metadata.handsetLocations = handsetLocations.trim() || undefined;
        metadata.portingRequired = portingRequired;
        metadata.numbersToPort = portingRequired && numbersToPort.trim()
          ? numbersToPort.split(/[,\n]/).map((n: string) => n.trim()).filter(Boolean)
          : undefined;
        metadata.portingStatus = portingStatus || undefined;
        metadata.callFlowNotes = callFlowNotes.trim() || undefined;
      }

      // --- Common metadata ---
      metadata.contractEndDate = contractEndDate || undefined;

      // Strip undefined keys (Firestore rejects them at the leaf level when we
      // do a partial update — safer to drop them than rely on `deleteField()`
      // for every cleared field).
      const cleanMetadata = Object.fromEntries(
        Object.entries(metadata).filter(([, v]) => v !== undefined)
      );

      await updateServiceMetadata(service.id, cleanMetadata);

      // Top-level fields (not in metadata)
      const topLevelUpdates: Record<string, string | null> = {
        updatedAt: new Date().toISOString(),
        updatedBy: localStorage.getItem('user_id') || 'unknown',
      };
      if (accountNumber.trim() !== (service.accountNumber || '')) {
        topLevelUpdates.accountNumber = accountNumber.trim() || null;
      }
      // Plan reassignment: picking a catalogue plan moves planId + planName
      // together; the free-text name only wins when no plan is picked (custom).
      const planChanged = !!selectedCatalogPlan && selectedCatalogPlan.id !== service.planId;
      if (planChanged && selectedCatalogPlan) {
        topLevelUpdates.planId = selectedCatalogPlan.id!;
        topLevelUpdates.planName = selectedCatalogPlan.displayName || selectedCatalogPlan.planName;
      } else if (planName.trim() !== (service.planName || '')) {
        topLevelUpdates.planName = planName.trim() || null;
      }
      if (notes !== (service.notes || '')) {
        topLevelUpdates.notes = notes || null;
      }

      // Service address (top-level fields, not metadata). Always written so a
      // cleared address persists as null (the card hides on falsy address).
      topLevelUpdates.address = addressSel.address.trim() || null;
      topLevelUpdates.city = addressSel.city.trim() || null;
      topLevelUpdates.state = addressSel.state.trim() || null;
      topLevelUpdates.zipCode = addressSel.zipCode.trim() || null;

      // Always write the timestamp at minimum so the service shows it was edited
      await updateDoc(doc(db, 'customer_services', service.id), topLevelUpdates);

      // Plan-driven price change goes through the audited price path so the
      // priceHistory trail explains why this customer's monthly moved.
      if (planChanged && planPriceChange && selectedCatalogPlan) {
        await updateServicePrice(
          service.id,
          selectedCatalogPlan.sellPrice,
          `Plan changed to ${selectedCatalogPlan.displayName || selectedCatalogPlan.planName}`,
          planPriceChange.from,
        );
      }

      // If the user entered a new address, fold it into the customer's saved
      // addresses (dedup-safe, best-effort — never blocks the service save).
      await commitServiceAddressToBook(service.customerId, addressSel, service.serviceType);

      onSaved?.();
      onClose();
    } catch (err) {
      console.error('Error updating service:', err);
      setError(err instanceof Error ? err.message : 'Failed to update service');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/70 z-[200] flex items-center justify-center p-4"
      onClick={onClose}
    >
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-gradient-to-br from-brand-primary to-brand-dark border border-brand-secondary/30 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto shadow-2xl"
        onClick={(e: React.MouseEvent<HTMLDivElement>) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-brand-secondary/20 to-transparent backdrop-blur-sm border-b border-white/10 p-6 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-full bg-gradient-to-br from-brand-secondary to-yellow-600 flex items-center justify-center shadow-lg">
              <Pencil className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Edit Service</h2>
              <p className="text-sm text-slate-400">
                {service.customerName} — {serviceType}
                {service.planName ? ` · ${service.planName}` : ''}
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-white" />
          </button>
        </div>

        {/* Form */}
        <div className="p-6 space-y-6">
          {error && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg text-red-400 text-sm">
              {error}
            </div>
          )}

          {/* Note about price */}
          <div className="p-3 bg-blue-500/10 border border-blue-500/30 rounded-lg text-blue-300 text-xs flex items-start gap-2">
            <Wifi className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <span>
              To change the monthly price, use the dedicated <strong>Edit Price</strong> button — it keeps an audit trail of every change.
            </span>
          </div>

          {/* Service Address */}
          <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-3">
            <h3 className="text-lg font-bold text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-brand-secondary" />
              Service Address
            </h3>
            <p className="text-xs text-slate-400 -mt-1">
              Where this service lives. A customer can have services at different
              sites — set each one's address here.
            </p>
            <ServiceAddressPicker
              key={service.id}
              customerId={service.customerId}
              value={addressSel}
              onChange={setAddressSel}
            />
          </div>

          {/* Starlink */}
          {serviceType === 'Starlink' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">Starlink Details</h3>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">Starlink Model</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setStarlinkModel('Standard')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      starlinkModel === 'Standard'
                        ? 'bg-indigo-600 text-white shadow-lg border-2 border-indigo-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Standard (Gen 3)
                  </button>
                  <button
                    type="button"
                    onClick={() => setStarlinkModel('Mini')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      starlinkModel === 'Mini'
                        ? 'bg-indigo-600 text-white shadow-lg border-2 border-indigo-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Starlink Mini
                  </button>
                </div>
                {/* Show legacy Gen 1/2/3 if currently set, so we can clear it */}
                {(starlinkModel === 'Gen 1' || starlinkModel === 'Gen 2' || starlinkModel === 'Gen 3') && (
                  <p className="text-xs text-amber-400 mt-2">
                    Legacy model "{starlinkModel}" — pick Standard or Mini above to update.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">Kit Number</label>
                <input
                  type="text"
                  value={kitNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setKitNumber(e.target.value)}
                  placeholder="e.g., UTxxxxxxxxxxxxxx-xxxxxxx"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">Starlink Account Email</label>
                <input
                  type="email"
                  value={starlinkAccountEmail}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStarlinkAccountEmail(e.target.value)}
                  placeholder="customer@example.com"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">Starlink Account Number</label>
                <input
                  type="text"
                  value={starlinkAccountNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setStarlinkAccountNumber(e.target.value)}
                  placeholder="ACC-XXXXXXX-XXXXX-XX"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors font-mono text-sm"
                />
                <p className="text-xs text-slate-400 mt-1">
                  Searchable from /services and global search. Also auto-populated by Starlink billing emails.
                </p>
              </div>
            </div>
          )}

          {/* 4G/5G */}
          {serviceType === '4G/5G' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">4G/5G Details</h3>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Carrier</label>
                <div className="grid grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => setCarrier('Telstra')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      carrier === 'Telstra'
                        ? 'bg-blue-600 text-white shadow-lg border-2 border-blue-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Telstra
                  </button>
                  <button
                    type="button"
                    onClick={() => setCarrier('Optus')}
                    className={`px-4 py-3 rounded-lg font-semibold transition-all ${
                      carrier === 'Optus'
                        ? 'bg-teal-600 text-white shadow-lg border-2 border-teal-400'
                        : 'bg-white/10 text-slate-300 hover:bg-white/20 border border-white/20'
                    }`}
                  >
                    Optus
                  </button>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">SIM Number</label>
                  <input
                    type="text"
                    value={simNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSimNumber(e.target.value)}
                    placeholder="SIM number"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Service Number</label>
                  <input
                    type="text"
                    value={serviceNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setServiceNumber(e.target.value)}
                    placeholder="e.g. 0402 729 756"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* NBN */}
          {serviceType === 'NBN' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">NBN Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">NBN Service ID</label>
                  <input
                    type="text"
                    value={nbnServiceId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNbnServiceId(e.target.value)}
                    placeholder="AVC000..."
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">NBN Location ID</label>
                  <input
                    type="text"
                    value={nbnLocationId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNbnLocationId(e.target.value)}
                    placeholder="LOC000..."
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Technology Type</label>
                <select
                  value={technology}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setTechnology(e.target.value)}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors"
                >
                  <option value="">Select technology</option>
                  <option value="FTTP">FTTP (Fiber to the Premises)</option>
                  <option value="FTTC">FTTC (Fiber to the Curb)</option>
                  <option value="FTTN">FTTN (Fiber to the Node)</option>
                  <option value="FTTB">FTTB (Fiber to the Building)</option>
                  <option value="HFC">HFC (Hybrid Fiber Coaxial)</option>
                  <option value="Fixed Wireless">Fixed Wireless</option>
                  <option value="Satellite">Satellite</option>
                </select>
              </div>
            </div>
          )}

          {/* OptiComm */}
          {serviceType === 'OptiComm' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">OptiComm Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">OptiComm Service ID</label>
                  <input
                    type="text"
                    value={opticommServiceId}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOpticommServiceId(e.target.value)}
                    placeholder="Service / NID"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">OptiComm Order Number</label>
                  <input
                    type="text"
                    value={opticommOrderNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setOpticommOrderNumber(e.target.value)}
                    placeholder="Order number"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* WiFi Extension */}
          {serviceType === 'WiFi Extension' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">WiFi Extension Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Access Point Count</label>
                  <input
                    type="number"
                    value={apCount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApCount(e.target.value)}
                    placeholder="e.g., 3"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Switch Model</label>
                  <input
                    type="text"
                    value={switchModel}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSwitchModel(e.target.value)}
                    placeholder="e.g., USW-Pro-24"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Access Point Serials (comma-separated)</label>
                <input
                  type="text"
                  value={apSerials}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setApSerials(e.target.value)}
                  placeholder="AP001, AP002, AP003"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Switch Serial</label>
                <input
                  type="text"
                  value={switchSerial}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSwitchSerial(e.target.value)}
                  placeholder="Switch serial number"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors font-mono text-sm"
                />
              </div>
            </div>
          )}

          {/* Smart Home */}
          {serviceType === 'Smart Home' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">Smart Home Details</h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Hub Brand</label>
                  <input
                    type="text"
                    value={hubBrand}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHubBrand(e.target.value)}
                    placeholder="e.g., Control4"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Hub Model</label>
                  <input
                    type="text"
                    value={hubModel}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHubModel(e.target.value)}
                    placeholder="e.g., EA-5"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Hub Serial Number</label>
                  <input
                    type="text"
                    value={hubSerial}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHubSerial(e.target.value)}
                    placeholder="Hub serial"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors font-mono text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Number of Devices</label>
                  <input
                    type="number"
                    value={deviceCount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setDeviceCount(e.target.value)}
                    placeholder="e.g., 10"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Security */}
          {serviceType === 'Security' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">Security System Details</h3>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Panel Serial Number</label>
                <input
                  type="text"
                  value={panelSerial}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPanelSerial(e.target.value)}
                  placeholder="Panel serial"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors font-mono text-sm"
                />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">NVR Model</label>
                  <input
                    type="text"
                    value={nvrModel}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNvrModel(e.target.value)}
                    placeholder="e.g., UNVR Pro"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">NVR Serial</label>
                  <input
                    type="text"
                    value={nvrSerial}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setNvrSerial(e.target.value)}
                    placeholder="NVR serial"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors font-mono text-sm"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Number of Cameras</label>
                  <input
                    type="number"
                    value={cameraCount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setCameraCount(e.target.value)}
                    placeholder="e.g., 4"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Number of Sensors</label>
                  <input
                    type="number"
                    value={sensorCount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSensorCount(e.target.value)}
                    placeholder="e.g., 8"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>
            </div>
          )}

          {/* VoIP */}
          {serviceType === 'VoIP' && (
            <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
              <h3 className="text-lg font-bold text-white">VoIP Details</h3>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Sasboss Customer Name</label>
                  <input
                    type="text"
                    value={sasbossCustomerName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setSasbossCustomerName(e.target.value)}
                    placeholder="Customer name in Sasboss portal"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Main Business Number</label>
                  <input
                    type="text"
                    value={mainNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setMainNumber(e.target.value)}
                    placeholder="e.g. 02 4000 0000"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Handsets Installed</label>
                  <input
                    type="number"
                    value={handsetCount}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHandsetCount(e.target.value)}
                    placeholder="e.g. 4"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-white mb-2">Handset Locations</label>
                  <input
                    type="text"
                    value={handsetLocations}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setHandsetLocations(e.target.value)}
                    placeholder="e.g. Reception x2, office x2"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="flex items-center gap-2 text-sm font-semibold text-white mb-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={portingRequired}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPortingRequired(e.target.checked)}
                    className="w-4 h-4 rounded border-white/20 bg-white/10 accent-brand-secondary"
                  />
                  Number Porting Required
                </label>
                {portingRequired && (
                  <div className="space-y-3 pl-6">
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Numbers to Port</label>
                      <textarea
                        value={numbersToPort}
                        onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNumbersToPort(e.target.value)}
                        placeholder="02 4000 0000, 02 4000 0001"
                        rows={2}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
                      />
                    </div>
                    <div>
                      <label className="block text-xs text-slate-400 mb-1">Porting Status</label>
                      <select
                        value={portingStatus}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => setPortingStatus(e.target.value as '' | 'pending' | 'submitted' | 'approved' | 'completed')}
                        className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors"
                      >
                        <option value="">Not started</option>
                        <option value="pending">Pending</option>
                        <option value="submitted">Submitted</option>
                        <option value="approved">Approved</option>
                        <option value="completed">Completed</option>
                      </select>
                    </div>
                  </div>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-white mb-2">Call Flow Notes</label>
                <textarea
                  value={callFlowNotes}
                  onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setCallFlowNotes(e.target.value)}
                  placeholder="Queues, ring groups, auto-attendant, after-hours routing..."
                  rows={3}
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
                />
              </div>
            </div>
          )}

          {/* Common */}
          <div className="bg-white/5 rounded-lg border border-white/10 p-4 space-y-4">
            <h3 className="text-lg font-bold text-white">Service Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Account Number</label>
                <input
                  type="text"
                  value={accountNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAccountNumber(e.target.value)}
                  placeholder="Account #"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-semibold text-white mb-2">Plan</label>
                {catalogPlans.length > 0 ? (
                  <select
                    value={selectedPlanId}
                    onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                      const id = e.target.value;
                      setSelectedPlanId(id);
                      const p = catalogPlans.find((pl) => pl.id === id);
                      if (p) setPlanName(p.displayName || p.planName);
                    }}
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors [&>option]:text-slate-900"
                  >
                    <option value="">Custom / keep name below</option>
                    {catalogPlans.map((p) => (
                      <option key={p.id} value={p.id}>
                        {planLabelWithSpeed(p)} — {formatCurrency(addGST(p.sellPrice), { minimumFractionDigits: 0, maximumFractionDigits: 0 })}/mo inc GST
                      </option>
                    ))}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={planName}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlanName(e.target.value)}
                    placeholder="e.g., Standard 100"
                    className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors"
                  />
                )}
              </div>
            </div>
            {catalogPlans.length > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div />
                <div>
                  {!selectedPlanId && (
                    <input
                      type="text"
                      value={planName}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => setPlanName(e.target.value)}
                      placeholder="Custom plan name"
                      className="w-full px-4 py-2.5 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors text-sm"
                    />
                  )}
                  {planPriceChange && (
                    <p className="mt-2 text-xs text-amber-300">
                      {/* Stored ex GST — shown inc GST, matching every other price surface. */}
                      Monthly price will change {planPriceChange.from !== null ? `${formatCurrency(addGST(planPriceChange.from), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}` : '(unset)'} → {formatCurrency(addGST(planPriceChange.to), { minimumFractionDigits: 2, maximumFractionDigits: 2 })} inc GST on save (recorded in price history).
                    </p>
                  )}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Contract End Date</label>
              <input
                type="date"
                value={contractEndDate}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setContractEndDate(e.target.value)}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white focus:outline-none focus:border-brand-secondary transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-semibold text-white mb-2">Service Notes</label>
              <textarea
                value={notes}
                onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) => setNotes(e.target.value)}
                placeholder="Any additional notes about this service..."
                rows={3}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:border-brand-secondary transition-colors resize-none"
              />
            </div>
          </div>

          {/* Actions */}
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white font-bold rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              disabled={isSubmitting}
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={isSubmitting}
              className="flex-1 px-6 py-3 bg-gradient-to-r from-brand-secondary to-yellow-600 hover:from-brand-secondary/90 hover:to-yellow-500 text-white font-bold rounded-lg transition-all shadow-lg hover:shadow-xl disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              <Save className="w-4 h-4" />
              {isSubmitting ? 'Saving...' : 'Save Changes'}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}
